/**
 * Right-side drawer that opens when a mesh node is clicked.
 *
 * Three sections:
 *   1. Node metadata    — framework, file:line, autonomy, model, tools, MCP/A2A
 *   2. Annotations      — owner / team / criticality / tags / notes, inline-editable
 *                         (POST /mesh/annotations/{agent_id_hint})
 *   3. Mis-detection    — one-click "Report mis-detection" → POST /mesh/feedback
 *
 * Synthetic nodes (mcp_server::, a2a_endpoint::) skip the annotation
 * and feedback sections because they don't correspond to a real
 * agent_id_hint backend-side.
 */
import { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import ReportProblemIcon from '@mui/icons-material/ReportProblemOutlined'
import { analysisApi } from '../../services/api'
import type { MeshEdge, MeshNode, OverlayMode } from './types'

/** §25 — an origin the server offers, and what it implies about who is
 *  answerable. Served rather than hardcoded so the vocabulary and its
 *  consequences live in one place. */
interface ModelOrigin {
  value: string
  obligation: string
  holds_weights: boolean
  requires_base_models: boolean
}

/** What has been declared about one model-layer component. `durability` says
 *  how much the declaration is worth given what it was pinned to: against a
 *  hostname it describes a door rather than a model. */
interface ModelDeclaration {
  identity: string
  component_kind: string
  origin: string
  base_models: string[]
  modifications: string[]
  notes: string | null
  obligation: string
  durability: string
  durability_reason: string
  declared_by: string | null
  declared_at: string | null
}

//: How many neighbours are listed per direction before the rest are summarised.
//  Named rather than inlined so the slice and the "+N more" line cannot drift
//  apart — they did, and the visible list stopped matching the count above it.
const MAX_NEIGHBOURS_SHOWN = 8

const CRITICALITY_VALUES = ['critical', 'high', 'normal', 'low']

const NODE_FEEDBACK_OPTIONS: { value: string; label: string }[] = [
  { value: 'not_an_agent', label: "This isn't really an agent" },
  { value: 'wrong_framework', label: 'Wrong framework attribution' },
  { value: 'wrong_autonomy', label: 'Wrong autonomy level' },
  { value: 'helpful', label: 'This detection is helpful' },
]

interface NodeDetailDrawerProps {
  node: MeshNode | null
  analysisId?: string
  onClose: () => void
  /**
   * Called after a successful annotation save so the parent can refresh
   * the mesh response and re-render the node with updated overlays.
   */
  onAnnotationSaved?: () => void
  /**
   * UX4 — active topology overlay. Drives the LEAD section of the drawer:
   *   risk         → findings list (filtered by agent_id_hint)
   *   blast_radius → downstream targets grouped by type
   *   drift        → ABF fingerprint history
   *   compliance   → per-control status table (lazy-loaded)
   *   framework    → framework-specific patterns
   * When omitted, the drawer behaves as it did pre-UX4.
   */
  overlay?: OverlayMode
  /** UX4 — full mesh edge list, used by Blast Radius and Drift sections
   *  to summarize this node's neighborhood without an extra backend call. */
  edges?: MeshEdge[]
  /** UX4 — full mesh node list, used to look up neighbor names + types. */
  nodes?: MeshNode[]
  /** UX4 — for Compliance lens drill-down (passed through to API). */
  complianceFramework?: string | null
  /** UX6 — deep link from the drawer's Findings row to the page's
   *  Findings tab filtered to this agent. Analysis scope only — at tenant
   *  scope no Findings tab exists in the parent, so this is omitted and
   *  the Findings row becomes non-clickable. */
  onViewFindings?: (agentIdHint: string) => void
  /** P1c — open cert timeline page (Investigate / Approve drift). */
  onOpenCertTimeline?: (certId: string) => void
  /** Escalation-path UI fix (2026-07-29) — ordered agent_id_hints of an
   *  active IACD-PERM-001 escalation path (from an entry-point agent
   *  through delegation hops to the agent holding elevated access), set
   *  when the user follows a "View escalation path" CTA from the
   *  Findings tab. Orthogonal to `overlay` (shown regardless of active
   *  lens) — an escalation path is a specific, transient answer to one
   *  finding, not a persistent tenant-wide lens the other overlays are. */
  escalationPath?: string[] | null
}

/** Names for the attributes a declaration can carry. Anything absent falls
 *  back to its own key with underscores removed, so a provider that declares
 *  something new is shown rather than dropped. */
/** What a scoping verdict means, for a reader who did not write the code. */
const SCOPING_HELP: Record<string, string> = {
  scoped: 'Every retrieval here filters to a tenant',
  unscoped: 'Retrievals here do not filter to a tenant',
  mixed: 'Some retrievals filter to a tenant and others do not',
  unknown: 'The query could not be read — not evidence either way',
}

const ATTRIBUTE_LABELS: Record<string, string> = {
  model: 'Embedding model',
  region: 'Region',
  retention: 'Retention (days)',
  engine: 'Engine version',
  produced: 'Built by training code in this estate',
}

/** §25. Read as plain answers rather than as the stored token, because the
 *  point of the field is what a reader should DO about it: a claim resting on
 *  a digest can be acted on, one resting on a hostname has to be confirmed. */
const IDENTITY_BASIS_LABELS: Record<string, string> = {
  artifact_digest: 'A content digest — names the same weights forever',
  registry_uri_version: 'A registry reference with a version',
  declared_manifest_id: 'A name declared in your own infrastructure',
  artifact_reference: 'A path or repository id — can be rebuilt with different contents',
  endpoint_and_model_string: 'A hostname — the weights behind it can change without notice',
}

const ATTRIBUTION_LABELS: Record<string, string> = {
  attributed: 'Yours, and the estate can say what it is',
  unattributed: 'Yours, and nothing here identifies the model',
  delegated: "A provider's to account for",
  not_assessable: 'Not assessable — this language has no structural support',
}

/** The origins a person picks between, ordered as the server orders them.
 *  Fetched at runtime so the list and its meaning live in one place; this is
 *  only the wording, and an origin the server adds still renders. */
const ORIGIN_LABELS: Record<string, string> = {
  undeclared: 'Not yet declared',
  vendor_hosted: "A vendor's model, on the vendor's infrastructure",
  third_party_weights: "Someone else's model, run by us",
  supplied_to_order: 'Built to order by a vendor or partner',
  adapted: 'Adapted from another model — fine-tuned, merged, distilled',
  built_in_house: 'Trained from nothing, here',
}

const OBLIGATION_LABELS: Record<string, string> = {
  provider: 'Provider of an AI system — documentation and data governance are yours',
  deployer: 'Deployer — the provider carries the documentation duties',
  contractual: 'Decided by your contract with whoever built it',
  unknown: 'Unknown until somebody declares where it came from',
}

export default function NodeDetailDrawer({
  node,
  analysisId,
  onClose,
  onAnnotationSaved,
  overlay = 'risk',
  edges = [],
  nodes = [],
  complianceFramework = null,
  onViewFindings,
  onOpenCertTimeline,
  escalationPath = null,
}: NodeDetailDrawerProps) {
  const open = node !== null

  // Annotation form local state
  const [owner, setOwner] = useState('')
  const [team, setTeam] = useState('')
  const [criticality, setCriticality] = useState('')
  const [tagsInput, setTagsInput] = useState('') // comma-separated UI
  const [notes, setNotes] = useState('')
  const [saving, setSaving] = useState(false)
  const [savedAt, setSavedAt] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Feedback form local state
  const [feedbackType, setFeedbackType] = useState('not_an_agent')
  const [feedbackReason, setFeedbackReason] = useState('')
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false)
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false)

  // Reset form whenever a new node opens
  useEffect(() => {
    setError(null)
    setSavedAt(null)
    setFeedbackSubmitted(false)
    setFeedbackReason('')
    if (!node) return
    const a = node.annotations
    setOwner(a?.owner || '')
    setTeam(a?.team || '')
    setCriticality(a?.criticality || '')
    setTagsInput((a?.tags || []).join(', '))
    setNotes(a?.notes || '')
  }, [node?.id])

  const isSynthetic = node?._source === 'synthetic'
  const canAnnotate = !isSynthetic && node?.type === 'agent'

  // §25. A model is a component and draws like any other, so it arrives here
  // as a knowledge_component. What it needs that a store does not is a place
  // to answer a question only the operator can: whose model is this.
  //
  // Not a page and not a tab. The lineage is edges on the canvas, the
  // undeclared list is PM-002 and the obligation flip is PM-001 — a
  // destination would be a second place to read facts that already have one.
  // What was missing is the ANSWER, and an answer belongs on the thing it is
  // about.
  const isModelLayer =
    node?.type === 'knowledge_component' &&
    (node.component_kind === 'inference_endpoint' ||
      node.component_kind === 'model_artifact')

  // UX4 — lazy-loaded data sources for lens-aware sections. Each is
  // fetched only when its lens is active AND the drawer is open on an
  // agent node (synthetic nodes have no ABF / compliance).
  // Risk lens no longer fetches findings here (UX6): the DETECTED
  // ATTRIBUTES Findings row already shows count + max severity, and a
  // "View N findings →" CTA deep-links to the Findings tab.
  const [abfHistory, setAbfHistory] = useState<unknown[] | null>(null)
  const [abfLoading, setAbfLoading] = useState(false)
  const [agentControls, setAgentControls] = useState<unknown[] | null>(null)
  const [agentControlsLoading, setAgentControlsLoading] = useState(false)

  // Reset lens-loaded state when node changes
  useEffect(() => {
    setAbfHistory(null)
    setAgentControls(null)
  }, [node?.id])

  // §25 — model provenance, declared on the component it is about.
  const [origins, setOrigins] = useState<ModelOrigin[] | null>(null)
  const [origin, setOrigin] = useState('')
  const [baseModels, setBaseModels] = useState('')
  const [modelNotes, setModelNotes] = useState('')
  const [declaration, setDeclaration] = useState<ModelDeclaration | null>(null)
  const [declaring, setDeclaring] = useState(false)

  // The vocabulary is fetched rather than hardcoded so the list of origins,
  // and what each one implies about who is answerable, lives in one place.
  useEffect(() => {
    if (!isModelLayer || origins !== null) return
    analysisApi
      .getModelVocabulary()
      .then((r) => setOrigins(r.data?.origins || []))
      .catch(() => setOrigins([]))
  }, [isModelLayer, origins])

  // What has already been declared about THIS component. Read from the
  // estate-wide list rather than a per-component route, because the same
  // call answers "what else is undeclared" and the drawer is usually opened
  // from a canvas the reader is working through.
  useEffect(() => {
    if (!isModelLayer || !node) {
      setDeclaration(null)
      return
    }
    let cancelled = false
    analysisApi
      .getModelDeclarations()
      .then((r) => {
        if (cancelled) return
        const match = (r.data?.declarations || []).find(
          (d: ModelDeclaration) => d.identity === node.name,
        )
        setDeclaration(match || null)
        setOrigin(match?.origin && match.origin !== 'undeclared' ? match.origin : '')
        setBaseModels((match?.base_models || []).join(', '))
        setModelNotes(match?.notes || '')
      })
      .catch(() => setDeclaration(null))
    return () => {
      cancelled = true
    }
  }, [isModelLayer, node?.id])

  const selectedOrigin = origins?.find((o) => o.value === origin)
  const parsedBaseModels = useMemo(
    () => baseModels.split(',').map((b) => b.trim()).filter(Boolean),
    [baseModels],
  )

  const onDeclareModel = async () => {
    if (!node || !node.component_kind) return
    setDeclaring(true)
    setError(null)
    try {
      const r = await analysisApi.declareModel(node.name, {
        component_kind: node.component_kind,
        origin,
        base_models: parsedBaseModels.length ? parsedBaseModels : null,
        notes: modelNotes || null,
      })
      setDeclaration(r.data)
      setSavedAt(new Date().toLocaleTimeString())
      onAnnotationSaved?.()
    } catch (e: unknown) {
      const detail =
        (e as { response?: { data?: { detail?: string } }; message?: string })?.response
          ?.data?.detail || (e as { message?: string })?.message
      setError(detail || 'Failed to record where this model came from')
    } finally {
      setDeclaring(false)
    }
  }

  // Drift lens — fetch ABF history
  useEffect(() => {
    if (!node || overlay !== 'drift' || node.type !== 'agent') return
    if (abfHistory !== null || abfLoading) return
    setAbfLoading(true)
    analysisApi
      .getAgentFingerprintHistory(node.id)
      .then((r) => {
        setAbfHistory(r.data?.fingerprints || r.data?.history || r.data || [])
      })
      .catch(() => setAbfHistory([]))
      .finally(() => setAbfLoading(false))
  }, [node?.id, overlay])

  // Compliance lens — fetch per-agent control list
  useEffect(() => {
    if (!node || overlay !== 'compliance' || node.type !== 'agent' || !complianceFramework) return
    if (agentControls !== null || agentControlsLoading) return
    setAgentControlsLoading(true)
    analysisApi
      .getMeshComplianceAgentControls({
        framework: complianceFramework,
        agent_id_hint: node.id,
        analysis_id: analysisId,
      })
      .then((r) => setAgentControls(r.data?.controls || []))
      .catch(() => setAgentControls([]))
      .finally(() => setAgentControlsLoading(false))
  }, [node?.id, overlay, complianceFramework, analysisId])

  // Blast Radius — derive neighbors client-side from mesh edges.
  // Capped per direction, and the cap is named so the "+N more" line and the
  // slice can never disagree about how many were left out.
  const blastNeighbors = useMemo(() => {
    if (!node || overlay !== 'blast_radius') return { outbound: [], inbound: [] }
    const nodeById = new Map(nodes.map((n) => [n.id, n]))
    const outbound: Array<{ edge: MeshEdge; target: MeshNode | undefined }> = []
    const inbound: Array<{ edge: MeshEdge; source: MeshNode | undefined }> = []
    for (const e of edges) {
      if (e.source === node.id) outbound.push({ edge: e, target: nodeById.get(e.target) })
      else if (e.target === node.id) inbound.push({ edge: e, source: nodeById.get(e.source) })
    }
    return { outbound, inbound }
  }, [node?.id, overlay, edges, nodes])

  const tags = useMemo(
    () =>
      tagsInput
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean),
    [tagsInput],
  )

  const onSaveAnnotation = async () => {
    if (!node) return
    setSaving(true)
    setError(null)
    try {
      await analysisApi.upsertMeshAnnotation(node.id, {
        owner: owner || null,
        team: team || null,
        criticality: criticality || null,
        tags: tags.length > 0 ? tags : null,
        notes: notes || null,
      })
      setSavedAt(new Date().toLocaleTimeString())
      onAnnotationSaved?.()
    } catch (e: unknown) {
      const detail =
        (e as { response?: { data?: { detail?: string } }; message?: string })?.response
          ?.data?.detail || (e as { message?: string })?.message
      setError(detail || 'Failed to save annotation')
    } finally {
      setSaving(false)
    }
  }

  const onSubmitFeedback = async () => {
    if (!node) return
    setFeedbackSubmitting(true)
    setError(null)
    try {
      await analysisApi.submitMeshFeedback({
        scope_type: 'node',
        scope_id: node.id,
        feedback_type: feedbackType,
        reason: feedbackReason || undefined,
        analysis_id: analysisId,
      })
      setFeedbackSubmitted(true)
    } catch (e: unknown) {
      const detail =
        (e as { response?: { data?: { detail?: string } }; message?: string })?.response
          ?.data?.detail || (e as { message?: string })?.message
      setError(detail || 'Failed to submit feedback')
    } finally {
      setFeedbackSubmitting(false)
    }
  }

  return (
    <Drawer anchor="right" open={open} onClose={onClose} variant="persistent">
      <Box sx={{ width: 420, p: 2, height: '100%', overflowY: 'auto' }}>
        {/* Header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              size="small"
              label={node?.type.replace('_', ' ') || ''}
              color={node?.type === 'agent' ? 'primary' : 'default'}
            />
            {node?.framework && (
              <Chip size="small" label={node.framework} variant="outlined" />
            )}
          </Stack>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
        <Typography variant="h6" sx={{ wordBreak: 'break-word' }}>
          {node?.name || '(unnamed)'}
        </Typography>
        {node?.file_path && (
          <Typography variant="caption" color="text.secondary">
            {node.file_path}
            {node.line_number ? `:${node.line_number}` : ''}
          </Typography>
        )}

        <Divider sx={{ my: 2 }} />

        {/* UX4 — Lens-aware lead section. Drawer content reorders based
            on the active topology overlay so the FIRST thing a user
            sees matches the question their lens is answering.
            Synthetic nodes skip this (no ABF / compliance).
            UX6 — Risk + Framework lenses don't render a lead section,
            because they duplicated the DETECTED ATTRIBUTES rows below.
            For Risk we add a "View N findings →" CTA inline with that
            Findings row instead. */}
        {node && !isSynthetic && node.type === 'agent' && (
          <LensLeadSection
            overlay={overlay}
            node={node}
            abfHistory={abfHistory}
            abfLoading={abfLoading}
            agentControls={agentControls}
            agentControlsLoading={agentControlsLoading}
            complianceFramework={complianceFramework}
            blastNeighbors={blastNeighbors}
            onOpenCertTimeline={onOpenCertTimeline}
            escalationPath={escalationPath}
          />
        )}

        {/* Section 1: metadata */}
        <Typography variant="overline" color="text.secondary">
          Detected attributes
        </Typography>
        <Stack spacing={0.5} sx={{ mt: 0.5 }}>
          {/* Phase A-D dependency-node specific attributes */}
          {node?.type === 'llm_model' && node.provider && (
            <Row k="Provider" v={node.provider} />
          )}
          {node?.type === 'llm_model' && node._synthesis_source && (
            <Row
              k="Source"
              v={node._synthesis_source === 'model_provider_only'
                ? 'Provider integration (model name unspecified)'
                : 'Scanner llm_model field'}
            />
          )}
          {node?.type === 'memory_store' && node.memory_category && (
            <Row k="Category" v={node.memory_category.replace('_', ' ')} />
          )}
          {node?.type === 'memory_store' && node.memory_type && (
            <Row k="Underlying" v={node.memory_type} />
          )}
          {node?.type === 'external_api' && node.api_kind && (
            <Row k="Kind" v={node.api_kind.replace('_', ' ')} />
          )}
          {node?.type === 'external_api' && node.api_domain && (
            <Row k="Domain" v={node.api_domain} />
          )}
          {/* Agent Reach — a knowledge-layer component. The classification is
              the customer's own label read out of their infrastructure, never
              ARIAS's judgement, and where it came from is stated because an
              inherited label is a weaker claim than a declared one. */}
          {node?.type === 'knowledge_component' && node.component_kind && (
            <Row k="Kind" v={node.component_kind.replace(/_/g, ' ')} />
          )}
          {node?.type === 'knowledge_component' && (
            <Row
              k="Declared in"
              v={node.declared_in && node.declared_in.length
                ? node.declared_in.join(', ')
                : 'No scanned repository declares it'}
            />
          )}
          {node?.type === 'knowledge_component' && node.classification && (
            <Row
              k="Classification"
              v={node.classification_inherited_from
                ? `${node.classification} (inherited from ${node.classification_inherited_from})`
                : node.classification}
            />
          )}
          {node?.type === 'knowledge_component' && node.cross_repo && (
            <Row k="Cross-repository" v="Used by agents outside the repository that declares it" />
          )}
          {/* What the declaration said about the component itself. All of this
              was extracted and stored and none of it was drawn — the node set
              `model` to None while the store had one, which is also why
              nothing could compare an index against the model that filled it.
              Rendered one row per attribute rather than as a blob, and only
              where present: 98 of 208 production components carry any. */}
          {node?.type === 'knowledge_component' && node.attributes && (
            Object.entries(node.attributes)
              .filter(([, v]) => v !== null && v !== undefined && v !== '')
              .map(([k, v]) => (
                <Row key={k} k={ATTRIBUTE_LABELS[k] || k.replace(/_/g, ' ')} v={String(v)} />
              ))
          )}
          {/* Named in a declaration, or inferred from what the code does with
              it. Stated for the same reason an inherited classification is:
              the reader is deciding whether to act on it. */}
          {node?.type === 'knowledge_component' && node.recognition === 'structural' && (
            <Row
              k="How we know"
              v="Inferred from how the code uses it — no declaration names it"
            />
          )}
          {/* §25. What the NAME rests on. A cross-repository link is only as
              good as the name both ends agree on: a content digest names one
              set of bits forever, an endpoint string names whatever is behind
              that host today. Both were computed and stored and neither was
              drawn, so the two looked identical on the canvas. */}
          {isModelLayer && node?.identity_basis && (
            <Row k="Identity rests on" v={IDENTITY_BASIS_LABELS[node.identity_basis]
              || node.identity_basis.replace(/_/g, ' ')} />
          )}
          {/* Whether the estate can account for the model behind it.
              `unattributed` is a finding rather than a coverage gap — it is
              what PM-002 reports — so it is stated plainly instead of being
              left to read as "we did not look". */}
          {isModelLayer && node?.attribution && (
            <Row k="Accounted for" v={ATTRIBUTION_LABELS[node.attribution]
              || node.attribution.replace(/_/g, ' ')} />
          )}
          {/* Where it runs, which is a different question from what built it.
              Rendered only when a platform was detected: no platform means
              self-hosted, and a row saying "none" would read as a gap. */}
          {node?.type === 'agent' && node.runtime_platform && (
            <Row k="Runs on" v={node.runtime_platform.replace(/_/g, ' ')} />
          )}
          {node?.type === 'agent' && node.runtime_platform && !!node.platform_services?.length && (
            <Row k="Platform services" v={node.platform_services.join(', ')} />
          )}
          {node?.type.startsWith('platform_') && node.platform && (
            <Row k="Provided by" v={node.platform.replace(/_/g, ' ')} />
          )}
          {/* Existing agent attributes */}
          {node?.autonomy_level && node?.type === 'agent' && (
            <Row k="Autonomy" v={node.autonomy_level} />
          )}
          {node?.model && node?.type === 'agent' && <Row k="Model" v={node.model} />}
          {node?.memory_type && node?.type === 'agent' && (
            <Row k="Memory" v={node.memory_type} />
          )}
          {node?.tools && node.tools.length > 0 && node?.type === 'agent' && (
            <Row k="Tools" v={`${node.tools.length} (${node.tools.slice(0, 3).join(', ')}${node.tools.length > 3 ? '…' : ''})`} />
          )}
          {node?.findings_count !== undefined && (
            <FindingsRow
              node={node}
              onViewFindings={onViewFindings}
            />
          )}
          {node?.definition_source && (
            <Row k="Source" v={node.definition_source} />
          )}
        </Stack>

        {/* §25. Where this model came from — the one thing on this drawer
            that the scanner cannot answer. Measured on the live attribution
            rules, a SageMaker endpoint and an Azure ML endpoint both resolve
            to "a provider's", and both are the ordinary way to serve a model
            you fine-tuned yourself. The question is not whose computer this
            is but whose model it is, and only the operator knows.

            Declaring clears PM-002; withdrawing reopens it. */}
        {isModelLayer && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="overline" color="text.secondary">
              Where this model came from
            </Typography>
            {declaration && declaration.origin !== 'undeclared' && (
              <Stack spacing={0.5} sx={{ mt: 1, mb: 1.5 }}>
                <Typography variant="body2">
                  {OBLIGATION_LABELS[declaration.obligation] || declaration.obligation}
                </Typography>
                {/* A declaration is only as good as what it is pinned to. On a
                    hostname it describes a door, and the weights behind that
                    door can be replaced with nothing in a scan changing — so
                    it is shown as provisional rather than with the confidence
                    of a digest. */}
                {declaration.durability !== 'durable' && (
                  <Typography variant="caption" color="warning.main">
                    {declaration.durability_reason}
                  </Typography>
                )}
                {declaration.declared_by && (
                  <Typography variant="caption" color="text.secondary">
                    Declared by {declaration.declared_by}
                    {declaration.declared_at ? ` · ${declaration.declared_at.slice(0, 10)}` : ''}
                  </Typography>
                )}
              </Stack>
            )}
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              <TextField
                select
                label="Origin"
                size="small"
                fullWidth
                value={origin}
                onChange={(e) => setOrigin(e.target.value)}
                helperText={
                  selectedOrigin
                    ? OBLIGATION_LABELS[selectedOrigin.obligation] || ''
                    : 'Nobody has said yet — which is what PM-002 reports'
                }
              >
                {(origins || [])
                  .filter((o) => o.value !== 'undeclared')
                  .map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {ORIGIN_LABELS[o.value] || o.value.replace(/_/g, ' ')}
                    </MenuItem>
                  ))}
              </TextField>
              {/* Required for an adapted model, because the whole value of the
                  declaration is which licence and which training-data terms
                  follow the derivative. A list, since a merge routinely
                  combines several upstreams. */}
              {selectedOrigin?.requires_base_models && (
                <TextField
                  label="Adapted from (comma-separated)"
                  size="small"
                  fullWidth
                  value={baseModels}
                  onChange={(e) => setBaseModels(e.target.value)}
                  placeholder="meta-llama/Llama-3-8B"
                  helperText="The base model's licence and training-data terms follow this one"
                />
              )}
              <TextField
                label="Notes"
                size="small"
                fullWidth
                multiline
                minRows={2}
                maxRows={4}
                value={modelNotes}
                onChange={(e) => setModelNotes(e.target.value)}
                placeholder="Where the weights live, which contract governs it, who to ask"
              />
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  size="small"
                  disabled={declaring || !origin}
                  onClick={onDeclareModel}
                >
                  {declaring ? 'Saving…' : 'Record this'}
                </Button>
                {declaration && declaration.origin !== 'undeclared' && (
                  <Button
                    size="small"
                    color="inherit"
                    disabled={declaring}
                    onClick={async () => {
                      if (!node?.component_kind) return
                      setDeclaring(true)
                      try {
                        await analysisApi.withdrawModelDeclaration(
                          node.name, node.component_kind,
                        )
                        setDeclaration(null)
                        setOrigin('')
                        onAnnotationSaved?.()
                      } finally {
                        setDeclaring(false)
                      }
                    }}
                  >
                    Withdraw
                  </Button>
                )}
              </Stack>
            </Stack>
          </>
        )}

        {/* Section 2: annotations (agent nodes only) */}
        {canAnnotate && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="overline" color="text.secondary">
              Annotations
            </Typography>
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              <TextField
                label="Owner"
                size="small"
                fullWidth
                value={owner}
                onChange={(e) => setOwner(e.target.value)}
                placeholder="alice@team.com"
              />
              <TextField
                label="Team"
                size="small"
                fullWidth
                value={team}
                onChange={(e) => setTeam(e.target.value)}
              />
              <TextField
                select
                label="Criticality"
                size="small"
                fullWidth
                value={criticality}
                onChange={(e) => setCriticality(e.target.value)}
              >
                <MenuItem value="">—</MenuItem>
                {CRITICALITY_VALUES.map((v) => (
                  <MenuItem key={v} value={v}>
                    {v}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Tags (comma-separated)"
                size="small"
                fullWidth
                value={tagsInput}
                onChange={(e) => setTagsInput(e.target.value)}
              />
              <TextField
                label="Notes"
                size="small"
                fullWidth
                multiline
                minRows={2}
                maxRows={6}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
              <Stack direction="row" spacing={1} alignItems="center">
                <Button
                  size="small"
                  variant="contained"
                  disabled={saving}
                  onClick={onSaveAnnotation}
                >
                  {saving ? 'Saving…' : 'Save annotation'}
                </Button>
                {savedAt && (
                  <Typography variant="caption" color="success.main">
                    Saved {savedAt}
                  </Typography>
                )}
              </Stack>
            </Stack>
          </>
        )}

        {/* Section 3: feedback (agent nodes only) */}
        {canAnnotate && (
          <>
            <Divider sx={{ my: 2 }} />
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <ReportProblemIcon fontSize="small" color="action" />
              <Typography variant="overline" color="text.secondary">
                Report mis-detection
              </Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Feedback feeds the continuous-improvement loop and helps the platform
              tune detection accuracy.
            </Typography>
            <Stack spacing={1}>
              <TextField
                select
                size="small"
                value={feedbackType}
                onChange={(e) => setFeedbackType(e.target.value)}
              >
                {NODE_FEEDBACK_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                placeholder="Optional context (what would be correct?)"
                fullWidth
                multiline
                minRows={2}
                value={feedbackReason}
                onChange={(e) => setFeedbackReason(e.target.value)}
              />
              <Stack direction="row" spacing={1} alignItems="center">
                <Button
                  size="small"
                  variant="outlined"
                  disabled={feedbackSubmitting || feedbackSubmitted}
                  onClick={onSubmitFeedback}
                >
                  {feedbackSubmitting
                    ? 'Submitting…'
                    : feedbackSubmitted
                      ? 'Thanks — recorded'
                      : 'Submit'}
                </Button>
                {feedbackSubmitted && (
                  <Tooltip title="Submit another by changing the type and clicking Submit again">
                    <Button size="small" onClick={() => setFeedbackSubmitted(false)}>
                      Submit another
                    </Button>
                  </Tooltip>
                )}
              </Stack>
            </Stack>
          </>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {isSynthetic && (
          <Alert severity="info" sx={{ mt: 2 }}>
            {syntheticNodeExplanation(node)}
          </Alert>
        )}
      </Box>
    </Drawer>
  )
}

/**
 * Type-aware explanation for synthetic nodes. The mesh synthesizes
 * several distinct "this isn't an agent we own" node types, each with
 * a different actionability story:
 *   - llm_model / tool / memory_store / external_api: never resolve
 *     into agents — they're dependencies, not actors.
 *   - mcp_server / a2a_endpoint: may resolve into agents if the
 *     receiving host is in a different repo the user could also scan.
 */
function syntheticNodeExplanation(node: MeshNode | null): string {
  if (!node) return ''
  switch (node.type) {
    case 'llm_model':
      return 'External LLM service ARIAS inferred from imports / configuration. This isn\'t an agent — it\'s the model your agents call. Will not "resolve" into an agent by scanning more repos.'
    case 'tool':
      return 'Tool function ARIAS detected on the scanned agents. This isn\'t an agent — it\'s a capability shared across agents.'
    case 'memory_store':
      return 'Memory subsystem (RAG / buffer / persistent state) ARIAS detected. This isn\'t an agent — it\'s how your agents persist context.'
    case 'external_api':
      return 'External service ARIAS detected from imports or config (database, third-party API, vector store). This isn\'t an agent — it\'s a dependency.'
    case 'mcp_server':
      return 'MCP server ARIAS inferred from a client reference in scanned code. The host\'s source code wasn\'t in any of your scanned repos. If it\'s a server you own, scan its repository to link the two sides. If it\'s a third-party MCP server (e.g., a known package like `mcp-server-memory` or `@modelcontextprotocol/server-github`), this stays as an inferred external dependency.'
    case 'a2a_endpoint':
      return 'A2A endpoint URL ARIAS detected from a client call. The host wasn\'t in any of your scanned repos. If it\'s an internal agent, scan its repo to convert this into a real agent node. If it\'s an external partner, it stays as an inferred dependency.'
    case 'platform_gateway':
      return 'A gateway provided by the hosting layer. It brokers which tools this agent can reach and under whose authority — which is why permission findings on agents behind it are scoped to what the repository shows rather than stated outright. It is infrastructure the agent depends on, not an agent.'
    case 'platform_memory':
      return 'Memory or session state provided by the hosting layer rather than by the agent\'s own code. It persists between invocations and lives outside this repository.'
    case 'platform_identity':
      return 'The workload identity the hosting layer issues to this agent. It determines what the agent can reach at runtime, and it is configured on the platform rather than in code.'
    case 'platform_service':
      return 'A service provided by the hosting layer that this agent depends on. Referenced in code; how it is configured lives on the platform.'
    case 'knowledge_component':
      return node.declared_in && node.declared_in.length
        ? `Knowledge-layer component declared in ${node.declared_in.join(', ')} and reached by agents here. This isn't an agent — it's what they retrieve from. It appears in this view because ARIAS reads every scanned repository together, not one at a time.`
        : 'Knowledge-layer component your agents reach, declared in no repository you have scanned. Usually the repository that owns it has not been scanned yet — scanning it will fill in what this is and what it holds.'
    default:
      return 'ARIAS inferred this from a reference in scanned code; the host wasn\'t in any of your scanned repos.'
  }
}


function Row({ k, v }: { k: string; v: string }) {
  return (
    <Stack direction="row" spacing={1} alignItems="baseline">
      <Typography variant="caption" sx={{ minWidth: 80, color: 'text.secondary' }}>
        {k}
      </Typography>
      <Typography variant="body2">{v}</Typography>
    </Stack>
  )
}


/**
 * UX6 — Findings row in DETECTED ATTRIBUTES. When `onViewFindings` is
 * provided (analysis scope), the value becomes a clickable button that
 * deep-links to the page's Findings tab filtered to this agent. At
 * tenant scope no callback is passed (no in-page Findings tab to
 * target) so the row is just text — identical to the pre-UX6 behavior.
 */
function FindingsRow({
  node,
  onViewFindings,
}: {
  node: MeshNode
  onViewFindings?: (agentIdHint: string) => void
}) {
  const totalCount = (node.findings_count || 0) + (node.affected_findings_count || 0)
  const summary = `${node.findings_count} direct${
    node.affected_findings_count > 0
      ? ` · ${node.affected_findings_count} multi-agent`
      : ''
  }${node.findings_severity_max ? ` · highest ${node.findings_severity_max}` : ''}`

  const clickable = Boolean(onViewFindings) && totalCount > 0
  return (
    <Stack direction="row" spacing={1} alignItems="baseline">
      <Typography variant="caption" sx={{ minWidth: 80, color: 'text.secondary' }}>
        Findings
      </Typography>
      <Typography variant="body2" sx={{ flex: 1 }}>{summary}</Typography>
      {clickable && (
        <Button
          size="small"
          variant="text"
          onClick={() => onViewFindings?.(node.id)}
          sx={{
            textTransform: 'none',
            minWidth: 0,
            px: 0.75,
            py: 0,
            fontSize: 11,
            lineHeight: 1.3,
          }}
        >
          View {totalCount} →
        </Button>
      )}
    </Stack>
  )
}


// ---------- UX4: Lens-aware lead section ----------------------------------

interface LensLeadSectionProps {
  overlay: OverlayMode
  node: MeshNode
  abfHistory: unknown[] | null
  abfLoading: boolean
  agentControls: unknown[] | null
  agentControlsLoading: boolean
  complianceFramework: string | null
  blastNeighbors: {
    outbound: Array<{ edge: MeshEdge; target: MeshNode | undefined }>
    inbound: Array<{ edge: MeshEdge; source: MeshNode | undefined }>
  }
  // P1c — when Drift lens is active and the agent has a cert, the
  // drawer offers Approve/Investigate actions. Parent passes the
  // navigate callback so the drawer doesn't need react-router.
  onOpenCertTimeline?: (certId: string) => void
  escalationPath?: string[] | null
}

// P1c — drift verdict chip palette. Mirrors LensLegend swatches.
const VERDICT_COLOR: Record<string, string> = {
  STABLE: '#9e9e9e',
  COSMETIC_DRIFT: '#ffc107',
  MATERIAL_DRIFT: '#fb8c00',
  CRITICAL_DRIFT: '#d32f2f',
  POSITIVE_DRIFT: '#388e3c',
  REBASELINE: '#1976d2',
}

const STATUS_COLOR: Record<string, string> = {
  aligned: '#388e3c',
  partial: '#fb8c00',
  gap: '#d32f2f',
  not_checked: '#bdbdbd',
}

// Escalation-path UI fix (2026-07-29) — thin wrapper around the
// existing per-overlay lead content. The path lead is orthogonal to
// `overlay` (see NodeDetailDrawerProps.escalationPath doc comment), so
// it's computed and rendered here, ahead of whichever overlay-specific
// body LensLeadSectionBody returns (including null for Risk/Framework —
// the path lead can still show even when the body renders nothing).
function LensLeadSection(props: LensLeadSectionProps) {
  const { escalationPath, node } = props
  const pathIdx = escalationPath ? escalationPath.indexOf(node.id) : -1
  const pathLead = pathIdx >= 0 && escalationPath ? (
    <Alert
      severity={pathIdx === escalationPath.length - 1 ? 'error' : 'info'}
      sx={{ mb: 1 }}
      icon={false}
    >
      {pathIdx === 0
        ? `Entry point of an active escalation path (${escalationPath.length - 1} hop${escalationPath.length - 1 !== 1 ? 's' : ''} to elevated access)`
        : pathIdx === escalationPath.length - 1
        ? 'Holds the elevated tier this escalation path reaches'
        : `Intermediate hop ${pathIdx} of ${escalationPath.length - 1} in an active escalation path`}
    </Alert>
  ) : null

  return (
    <>
      {pathLead}
      <LensLeadSectionBody {...props} />
    </>
  )
}

function LensLeadSectionBody({
  overlay,
  node,
  abfHistory,
  abfLoading,
  agentControls,
  agentControlsLoading,
  complianceFramework,
  blastNeighbors,
  onOpenCertTimeline,
}: LensLeadSectionProps) {
  // UX6 — Risk and Framework lenses intentionally render NO lead
  // section here. Their data already lives in DETECTED ATTRIBUTES
  // below (findings counts + framework / model / tools rows). The
  // Risk lens adds a clickable "View N findings →" affordance to the
  // Findings row in the metadata section instead.
  if (overlay === 'risk' || overlay === 'framework') return null

  if (overlay === 'blast_radius') {
    const { outbound, inbound } = blastNeighbors
    // What the reach amounts to. The counts below say how many things this
    // touches; without this a node reaching ten read-only tools outranks one
    // that can delete, which is the opposite of the question this lens asks.
    const capability = node?.blast_capability

    // Both directions, each in its own section, each with the arrow that
    // matches it. The counts were reported for both and only outbound was
    // ever listed, under a `→` — so an agent that five things depended on
    // read as an agent with five dependencies pointing the other way, and
    // the number never matched the list.
    const section = (
      title: string,
      hint: string,
      rows: Array<{ edge: MeshEdge; other: MeshNode | undefined }>,
      arrow: string,
      empty: string,
    ) => (
      <Box sx={{ mt: 1.5 }}>
        <Stack direction="row" spacing={0.75} alignItems="baseline">
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {rows.length} · {hint}
          </Typography>
        </Stack>
        {rows.length === 0 ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {empty}
          </Typography>
        ) : (
          <Stack spacing={0.5} sx={{ mt: 0.5 }}>
            {rows.slice(0, MAX_NEIGHBOURS_SHOWN).map(({ edge, other }, i) => (
              <Stack key={i} direction="row" spacing={1} alignItems="baseline">
                <Chip
                  size="small"
                  label={edge.type.replace(/_/g, ' ')}
                  variant="outlined"
                  sx={{ height: 18, fontSize: 10 }}
                />
                <Typography variant="caption" sx={{ flex: 1, wordBreak: 'break-word' }}>
                  <Box component="span" sx={{ fontFamily: 'monospace', mr: 0.5 }}>
                    {arrow}
                  </Box>
                  {other?.name || edge.target}
                </Typography>
                {/* Whether the retrieval bounded itself to a tenant. Shown
                    only where a verdict exists: a reach that made no retrieval
                    has no opinion, and rendering that as "unscoped" would
                    accuse a customer of something nobody observed.
                    `constant` is called out by name because it is the case
                    that passes an is-it-filtered check and fails the moment
                    there is a second tenant. */}
                {/* Reached through a server rather than directly. Named
                    because the fix for an unscoped access is usually on the
                    server that fronts the store, not on the agent that
                    called it. */}
                {edge.via && (
                  <Typography variant="caption" color="text.secondary">
                    via {edge.via}
                  </Typography>
                )}
                {edge.scoping && (
                  <Typography
                    variant="caption"
                    color={edge.scoping === 'scoped' && edge.scoped_by !== 'constant'
                      ? 'text.secondary' : 'warning.main'}
                    title={SCOPING_HELP[edge.scoping] || undefined}
                  >
                    {edge.scoping === 'scoped' && edge.scoped_by
                      ? `scoped by ${edge.scoped_by}`
                      : edge.scoping}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary">
                  {edge.observed ? 'observed' : 'inferred'}
                </Typography>
              </Stack>
            ))}
            {/* Says how many are not listed rather than leaving the reader to
                subtract a visible list from a headline number. */}
            {rows.length > MAX_NEIGHBOURS_SHOWN && (
              <Typography variant="caption" color="text.secondary">
                +{rows.length - MAX_NEIGHBOURS_SHOWN} more not listed
              </Typography>
            )}
          </Stack>
        )}
      </Box>
    )

    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="overline" color="text.secondary">
          Blast radius — if this misbehaves…
        </Typography>
        {capability && (
          <Box sx={{ mt: 0.75, mb: 0.5 }}>
            <Chip
              size="small"
              label={`Can ${capability} through what it reaches`}
              color={capability === 'delete' || capability === 'execute' ? 'error' : 'default'}
              variant={capability === 'delete' || capability === 'execute' ? 'filled' : 'outlined'}
            />
          </Box>
        )}
        {outbound.length === 0 && inbound.length === 0 && (
          <Alert severity="info" sx={{ mt: 1 }} icon={false}>
            Isolated node — nothing detected in either direction.
          </Alert>
        )}
        {section(
          'Reaches', 'what this depends on',
          outbound.map(({ edge, target }) => ({ edge, other: target })),
          '→', 'Nothing downstream — this depends on nothing we detected.',
        )}
        {section(
          'Reached by', 'what depends on this',
          inbound.map(({ edge, source }) => ({ edge, other: source })),
          '←', 'Nothing upstream — nothing we detected depends on this.',
        )}
      </Box>
    )
  }

  if (overlay === 'drift') {
    const hist = (abfHistory || []) as Array<Record<string, unknown>>
    const certId = node.certificate_id
    const effStatus = node.certificate_effective_status
    const verdict = node.drift_verdict
    const verdictLabel = verdict ? verdict.replace('_', ' ').toLowerCase() : null
    const suspended = effStatus === 'suspended'
    return (
      <Box sx={{ mb: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
          <Typography variant="overline" color="text.secondary">
            Drift — recent fingerprint changes
          </Typography>
          {verdictLabel && verdict && (
            <Chip
              size="small"
              label={verdictLabel}
              sx={{
                height: 18,
                fontSize: 10,
                bgcolor: VERDICT_COLOR[verdict] || '#90a4ae',
                color: 'white',
                fontWeight: 600,
              }}
            />
          )}
          {node.drift_improvement === 'POSITIVE_DRIFT' && (
            <Chip
              size="small"
              label="improved"
              sx={{ height: 18, fontSize: 10, bgcolor: '#388e3c', color: 'white' }}
            />
          )}
        </Stack>
        {certId && (
          <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => onOpenCertTimeline?.(certId)}
              sx={{ textTransform: 'none', fontSize: 11, py: 0.25 }}
            >
              Investigate →
            </Button>
            {suspended && (
              <Button
                size="small"
                variant="contained"
                color="primary"
                onClick={() => onOpenCertTimeline?.(certId)}
                sx={{ textTransform: 'none', fontSize: 11, py: 0.25 }}
              >
                Approve drift
              </Button>
            )}
          </Stack>
        )}
        {abfLoading ? (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
            <CircularProgress size={14} />
            <Typography variant="caption" color="text.secondary">
              Loading fingerprint history…
            </Typography>
          </Stack>
        ) : hist.length === 0 ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            No fingerprint history yet — this agent has only been seen in one scan.
          </Typography>
        ) : (
          <Stack spacing={0.5} sx={{ mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Last <strong>{Math.min(hist.length, 5)}</strong> of <strong>{hist.length}</strong> fingerprints:
            </Typography>
            {hist.slice(0, 5).map((h, i) => {
              const hash = String(h.fingerprint_hash || h.hash || '')
              const prev = hist[i + 1]
              const prevHash = prev ? String(prev.fingerprint_hash || prev.hash || '') : ''
              const changed = prevHash && hash !== prevHash
              const at = String(h.computed_at || h.created_at || h.timestamp || '')
              return (
                <Stack key={i} direction="row" spacing={1} alignItems="baseline">
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      bgcolor: changed ? '#fb8c00' : '#bdbdbd',
                      mt: '4px',
                    }}
                  />
                  <Typography variant="caption" sx={{ flex: 1 }}>
                    {at ? new Date(at).toLocaleString() : '(unknown time)'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                    {hash.slice(0, 8)}
                  </Typography>
                </Stack>
              )
            })}
          </Stack>
        )}
      </Box>
    )
  }

  if (overlay === 'compliance') {
    if (!complianceFramework) {
      return (
        <Box sx={{ mb: 2 }}>
          <Typography variant="overline" color="text.secondary">
            Compliance
          </Typography>
          <Alert severity="info" sx={{ mt: 1 }} icon={false}>
            Pick a framework above to see this agent's per-control status.
          </Alert>
        </Box>
      )
    }
    const ctrls = (agentControls || []) as Array<Record<string, unknown>>
    return (
      <Box sx={{ mb: 2 }}>
        <Typography variant="overline" color="text.secondary">
          {complianceFramework} — per-control status
        </Typography>
        {agentControlsLoading ? (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
            <CircularProgress size={14} />
            <Typography variant="caption" color="text.secondary">
              Loading control status…
            </Typography>
          </Stack>
        ) : ctrls.length === 0 ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            No controls in this framework map to detected findings on this agent.
          </Typography>
        ) : (
          <Box sx={{ mt: 1, maxHeight: 220, overflowY: 'auto' }}>
            <Table size="small" sx={{ '& td, & th': { px: 1, py: 0.5, fontSize: 11 } }}>
              <TableHead>
                <TableRow>
                  <TableCell>Control</TableCell>
                  <TableCell align="right">Findings</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ctrls.slice(0, 30).map((c, i) => {
                  const status = String(c.status || 'not_checked')
                  return (
                    <TableRow key={i}>
                      <TableCell>
                        <Tooltip title={String(c.control_title || '')} placement="top">
                          <Typography variant="caption" sx={{ fontWeight: 600 }}>
                            {String(c.control_id || '')}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="caption">{Number(c.finding_count || 0)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={status}
                          sx={{
                            height: 18,
                            fontSize: 10,
                            bgcolor: STATUS_COLOR[status] || '#bdbdbd',
                            color: 'white',
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
            {ctrls.length > 30 && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                +{ctrls.length - 30} more controls
              </Typography>
            )}
          </Box>
        )}
      </Box>
    )
  }

  return null
}
